/**
 * Campaign.jsx
 * One campaign: the week laid out in the order it will go out.
 *
 * The whole point of the screen is the last button on it. Everything above is
 * there so that pressing "Approve week" is an informed act rather than a leap:
 * every caption is visible and editable, every time can be moved, and anything
 * that would stop a post going out is named before you commit to it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api.js';
import CampaignPost from '../components/CampaignPost.jsx';
import ImageLightbox from '../components/ImageLightbox.jsx';
import { Check, Clock, Warning } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

export default function Campaign() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Set when arriving straight from planning: what it cost, and any files
  // that were left out because a platform would refuse them.
  const justPlanned = useLocation().state;

  const [campaign, setCampaign] = useState(null);
  const [brand, setBrand] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.campaigns.get(id);
      setCampaign(data.campaign);
      setBrand(data.brand);
      setPosts(data.posts);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function approve() {
    const drafts = posts.filter((p) => p.status === 'draft').length;
    if (!confirm(`Schedule ${drafts} post${drafts === 1 ? '' : 's'} for ${brand?.name}?`)) return;

    setBusy(true);
    setError(null);
    try {
      const result = await api.campaigns.approve(id);
      setCampaign(result.campaign);
      setPosts(result.posts);
      setOutcome(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function cancelAll() {
    if (!confirm('Return every scheduled post in this campaign to a draft?')) return;
    setBusy(true);
    try {
      const result = await api.campaigns.cancel(id);
      setCampaign(result.campaign);
      setPosts(result.posts);
      setOutcome(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="mono">Loading…</p>;
  if (!campaign) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error?.message ?? 'That campaign does not exist.'}</span>
      </div>
    );
  }

  const drafts = posts.filter((p) => p.status === 'draft');
  const scheduled = posts.filter((p) => p.status === 'scheduled');
  const published = posts.filter((p) => p.status === 'published');

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">
            <button className="linkish" onClick={() => navigate('/campaigns')}>
              Campaigns
            </button>{' '}
            / {brand?.name}
          </div>
          <h1>{campaign.name}</h1>
          <p>
            {campaign.prompt || 'No brief recorded.'}
          </p>
        </div>
        <div className="page-head__actions">
          {scheduled.length > 0 && (
            <button className="btn" onClick={cancelAll} disabled={busy}>
              Unschedule all
            </button>
          )}
          <button
            className="btn btn--primary"
            onClick={approve}
            disabled={busy || drafts.length === 0}
            title={drafts.length === 0 ? 'Nothing left to approve' : ''}
          >
            {busy ? 'Scheduling…' : `Approve week (${drafts.length})`}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {justPlanned?.rejectedMedia?.length > 0 && (
        <div className="notice notice--error">
          <Warning />
          <div>
            <span>
              {justPlanned.rejectedMedia.length} file
              {justPlanned.rejectedMedia.length === 1 ? ' was' : 's were'} left out of this
              campaign:
            </span>
            <ul className="reasons">
              {justPlanned.rejectedMedia.map((m) => (
                <li key={m.id}>{m.reason}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {outcome && (
        <div className={`notice ${outcome.blocked ? 'notice--error' : 'notice--info'}`}>
          {outcome.blocked ? <Warning /> : <Check />}
          <div>
            <span>
              {outcome.scheduled} post{outcome.scheduled === 1 ? '' : 's'} scheduled
              {outcome.blocked ? `, ${outcome.blocked} held back:` : '.'}
            </span>
            {outcome.blocked > 0 && (
              <ul className="reasons">
                {outcome.results
                  .filter((r) => !r.ok && !r.skipped)
                  .map((r) => (
                    <li key={r.postId}>
                      <strong>{r.title || 'Untitled post'}</strong> — {r.reason}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="stat-row">
        <Stat label="Posts" value={posts.length} />
        <Stat label="Drafted" value={drafts.length} />
        <Stat label="Scheduled" value={scheduled.length} />
        <Stat label="Published" value={published.length} />
      </div>

      {posts.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty__art">
              <Clock size={30} />
            </div>
            <h3>This campaign has no posts</h3>
            <p>Its drafts may have been deleted from the Drafts screen.</p>
          </div>
        </div>
      ) : (
        <div className="week">
          {posts.map((post, i) => (
            <CampaignPost
              key={post.id}
              index={i + 1}
              post={post}
              onChanged={load}
              onView={setViewing}
              onError={setError}
            />
          ))}
        </div>
      )}

      {campaign.approved_at && (
        <p className="hint" style={{ marginTop: 16 }}>
          Approved {formatLondon(campaign.approved_at)}. Scheduled posts publish on their own; the
          Dashboard shows anything that fails.
        </p>
      )}

      {viewing && (
        <ImageLightbox
          src={api.media.rawUrl(viewing.id)}
          isVideo={viewing.kind === 'video'}
          alt={viewing.alt_text || ''}
          caption={`${viewing.kind === 'video' ? 'Video' : 'Image'}${
            viewing.width ? ` · ${viewing.width}×${viewing.height}` : ''
          }`}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}
